/**
 * Step 3's plans — cashflow & Age Pension on the shared spine (cashflow rebuild, 21 Oct 2026; the agreed
 * mock-up's j1-plans, and the village's RvPlansView).
 *
 *  - Changing nothing as a strip of the four numbers from step 1: every card is read against it.
 *  - One line saying what the best plan does against changing nothing — or, plainly, that none leaves the
 *    family more (cross-module change 5: a verdict line when no plan beats doing nothing).
 *  - "What matters most to you?" — the four goals; choosing one never re-runs the engine. "Highest Age
 *    Pension" is always shown, and opens to the reason when no plan lifts it.
 *  - The cards side by side, aligned row by row: the plan in words, what is left to the family against
 *    changing nothing, the Age Pension and each year's money (each with "why?"), when the money runs out, an
 *    "Important" or "Worth knowing" line, and Explain this / What could go wrong / Choose this plan.
 *  - The ways on: compare every way side by side, things that change the answer, build a plan yourself.
 *
 * Presentation only. Every figure is the engine row's own; the ranking is the shared rule
 * (cashflowPensionRanking.bestForEachGoal) with the plan choice rule on top (planChoiceRule.ts).
 */
import React from 'react';
import EnginePlanCard, { type PlanCardNumber } from '../agedCare/EnginePlanCard.tsx';
import EngineGoalControl, { type GoalFocusId } from '../agedCare/EngineGoalControl.tsx';
import DialogShell from '../../components/ui/DialogShell.tsx';
import { householdVoice } from '../../utils/householdWords.ts';
import { planRunwayCopy } from '../../components/insights/fundingSummary.tsx';
import { bestForEachGoal } from '../../../../shared/src/calculation/cashflowPensionRanking.ts';
import { leadWithinTie, leadExceptionOf, leadChip, leadLine, pickSecondPlan, secondPlanLine, secondPlanChip, allPlansOpenLine, type PlanReadings, type LeadException, type SecondPlan } from '../../strategy/planChoiceRule.ts';
import { pickCashflowCoveredLeader } from '../../strategy/cashflowCoveredGoal.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import type { CashflowPensionResult, SupportCostInput } from './cashflowPensionScenario.ts';
import type { CashflowPensionCandidate } from './cashflowPensionCandidates.ts';
import { cpPlanWords, cpKindsOf, cpTiedUpOf, cpAmountsOf, cpPensionWhyOf, cpPensionWhyWords, cpSpentOnWords } from './cpPlanWords.ts';
import { runwayCardLine } from '../agedCare/moneyStory.tsx';
import { BreakdownList, money, signed, cashWords } from './cpUi.tsx';
import OtherGoalLeaders from '../../components/strategy/OtherGoalLeaders.tsx';
import { benchmarkWords, householdRents } from '../../utils/benchmarkWords.ts';
import { ChosenAdviceLine } from '../../components/strategy/ChosenAdviceLine.tsx';

/** Why an option the engine could not work out cannot be chosen — in plain words (review, 18 Oct 2026). */
export const CANNOT_CHOOSE_ERRORED = 'We couldn’t work this one out on your figures, so it can’t be chosen, saved or used for advice. The other options are not affected.';

export interface CpLeaders {
    base: CashflowPensionResult | null;
    usable: CashflowPensionResult[];
    estate: CashflowPensionResult | null;
    /** True when the estate leader leaves the family more than changing nothing. */
    estateBeats: boolean;
    exception: LeadException | null;
    second: SecondPlan<CashflowPensionResult> | null;
    covered: CashflowPensionResult | null;
    cashflow: CashflowPensionResult | null;
    pension: CashflowPensionResult | null;
}

/**
 * Which plan leads each goal. The shared ranking gates each goal on beating changing nothing by more than
 * $100; the estate goal then applies the plan choice rule (a near-tie goes to the plan without a step that
 * cannot be undone), and when nothing beats changing nothing the plan that leaves the most is still shown —
 * as the closest, never as "Leaves the most".
 */
export function cpLeadersOf(results: CashflowPensionResult[] | null | undefined, candidates: ReadonlyArray<CashflowPensionCandidate>): CpLeaders {
    const list = results || [];
    const base = list.find((r) => r.changeNothing && !r.error) ?? null;
    const usable = list.filter((r) => !r.changeNothing && !r.error);
    const cand = (r: CashflowPensionResult) => candidates.find((c) => c.id === r.id) ?? null;
    const readings: PlanReadings<CashflowPensionResult> = {
        estate: (r) => r.snapshot.estate,
        moved: (r) => cpTiedUpOf(cand(r)),
        cashflow: (r) => r.snapshot.cashflow,
        kinds: (r) => cpKindsOf(cand(r) ?? { id: r.id }),
    };
    const winners = bestForEachGoal(list);
    const gated = usable.filter((r) => !base || r.snapshot.estate - base.snapshot.estate > 100);
    let estate: CashflowPensionResult | null = null;
    let exception: LeadException | null = null;
    let estateBeats = false;
    if (winners.estate && gated.length) {
        estate = leadWithinTie(gated, readings) ?? winners.estate;
        exception = leadExceptionOf(gated, estate, readings);
        estateBeats = true;
    } else if (usable.length) {
        estate = usable.reduce((t, r) => (r.snapshot.estate > t.snapshot.estate ? r : t), usable[0]!);
    }
    const second = estate ? pickSecondPlan(usable, estate, readings, (a, b) => a.id === b.id) : null;
    const coveredPick = pickCashflowCoveredLeader(
        usable.map((r) => ({ name: r.id, row: r, snapshot: { netCashflow: r.snapshot.cashflow, legacyValue: r.snapshot.estate }, projections: r.projections })),
    );
    return { base, usable, estate, estateBeats, exception, second, covered: coveredPick?.strategy.row ?? null, cashflow: winners.cashflow, pension: winners.pension };
}

export interface CpPlansViewProps {
    factFind: DraftFactFind;
    results: CashflowPensionResult[];
    candidates: CashflowPensionCandidate[];
    horizonYears: number;
    /** What the household owns today (step 1's third number). */
    ownToday: number;
    supportCost: SupportCostInput | null;
    focus: GoalFocusId;
    onFocus: (f: GoalFocusId) => void;
    chosenId: string | null;
    onChoose: (row: CashflowPensionResult) => void;
    onExplain: (row: CashflowPensionResult) => void;
    onRisks: (row: CashflowPensionResult) => void;
    onAdvice: (row: CashflowPensionResult) => void;
    onBuildYourOwn: (row: CashflowPensionResult | null) => void;
    onChanges: () => void;
    renderDetail: (row: CashflowPensionResult) => React.ReactNode;
    renderFamily: (row: CashflowPensionResult) => React.ReactNode;
    /** What must be confirmed before a row can be chosen (income only), and whether it is held. */
    beforeChoose: (row: CashflowPensionResult) => React.ReactNode | undefined;
    chooseHeld: (row: CashflowPensionResult) => boolean;
    /** Above everything: the chosen banner, or the note that the chosen plan was priced differently. */
    notices?: React.ReactNode;
    /** A card whose detail opens with the screen (the plan page's "Enter the quote"). */
    initialOpenDetail?: string | null;
}

const CpPlansView = ({
    factFind, results, candidates, horizonYears, ownToday, supportCost, focus, onFocus, chosenId, onChoose, onExplain, onRisks, onAdvice,
    onBuildYourOwn, onChanges, renderDetail, renderFamily, beforeChoose, chooseHeld, notices, initialOpenDetail = null,
}: CpPlansViewProps) => {
    const v = householdVoice(factFind);
    const yrs = `${horizonYears} ${horizonYears === 1 ? 'year' : 'years'}`;
    const [openDetail, setOpenDetail] = React.useState<string | null>(initialOpenDetail);
    const [why, setWhy] = React.useState<{ row: CashflowPensionResult; page: 'pension' | 'cashflow' } | null>(null);
    const [comparing, setComparing] = React.useState(false);
    const L = React.useMemo(() => cpLeadersOf(results, candidates), [results, candidates]);
    const base = L.base;
    const cand = (r: CashflowPensionResult) => candidates.find((c) => c.id === r.id) ?? { id: r.id };
    const words = (r: CashflowPensionResult) => cpPlanWords(cand(r), factFind);
    const estateOf = (r: CashflowPensionResult | null) => Number(r?.snapshot.estate) || 0;
    const pensionOf = (r: CashflowPensionResult | null) => Number(r?.snapshot.pension) || 0;
    const cashOf = (r: CashflowPensionResult | null) => Number(r?.snapshot.cashflow) || 0;
    const tested = results.filter((r) => !r.changeNothing).length;
    const coveredSame = !!L.covered && !!L.estate && L.covered.id === L.estate.id;
    const effective: GoalFocusId = focus === 'covered' && coveredSame ? 'legacy' : focus;
    const isCouple = factFind?.adviceFor === 'couple';

    const leaderFor = (f: GoalFocusId) => (f === 'legacy' ? L.estate : f === 'covered' ? L.covered : f === 'cashflow' ? L.cashflow : L.pension);
    const leader = leaderFor(effective);
    /*
     * ONE CARD PER GOAL, A SECOND ONLY WITH A PURPOSE (owner, 22 Oct 2026: "each section has a specific purpose … only
     * show the second card when there is a purpose to it"). The second card is the plan choice rule's near-tie beside
     * the estate leader (nearly the same estate, less locked away or a better cashflow — planChoiceRule.ts), as aged
     * care and support at home do. Another goal's leader is pointed to in one line under the cards (OtherGoalLeaders).
     */
    const partner = leader && effective === 'legacy' && L.second && L.second.strategy.id !== leader.id ? L.second.strategy : null;
    const noPensionLift = effective === 'pension' && !L.pension;
    const shown = noPensionLift || !leader ? [] : [leader, ...(partner ? [partner] : [])];
    const others = (['legacy', 'cashflow', 'pension'] as const)
        .filter((g) => g !== effective)
        .map((g) => ({ g, row: leaderFor(g) }))
        .filter((x) => !!x.row && !shown.some((s) => s.id === x.row!.id))
        .map((x) => ({ goal: x.g, title: words(x.row!).title, legacy: estateOf(x.row), cashflow: cashOf(x.row), pension: pensionOf(x.row) }));

    const chipsFor = (r: CashflowPensionResult): string | null => {
        const chips: string[] = [];
        if (L.estate?.id === r.id) chips.push(L.estateBeats ? leadChip(L.exception) : 'The closest to changing nothing');
        if (L.second?.strategy.id === r.id && effective === 'legacy') chips.push(secondPlanChip(L.second));
        if (L.covered?.id === r.id && !coveredSame) chips.push('Income covers the costs');
        if (L.cashflow?.id === r.id) chips.push('Most left over each year');
        if (L.pension?.id === r.id) chips.push('Highest Age Pension');
        return chips.length ? chips.join(' · ') : null;
    };

    const maxGain = Math.max(1, ...shown.map((s) => Math.abs(estateOf(s) - estateOf(base))));
    const maxTied = Math.max(1, ...shown.map((s) => cpTiedUpOf(cand(s))));

    const card = (row: CashflowPensionResult) => {
        const w = words(row);
        const m = cpAmountsOf(cand(row));
        const delta = base ? estateOf(row) - estateOf(base) : null;
        const pDelta = base ? pensionOf(row) - pensionOf(base) : 0;
        const cDelta = base ? cashOf(row) - cashOf(base) : 0;
        const tied = cpTiedUpOf(cand(row));
        const numbers: PlanCardNumber[] = [
            {
                label: 'Age Pension', value: pensionOf(row), unit: 'a year',
                vs: base && Math.abs(pDelta) >= 1
                    ? <><span className={`font-bold ${pDelta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(pDelta)} a year</span> vs changing nothing</>
                    : 'the same as changing nothing',
                tone: pDelta >= 1 ? 'good' : 'neutral',
                onWhy: () => setWhy({ row, page: 'pension' }),
            },
            {
                label: cashOf(row) < 0 ? 'Each year, short by' : 'Left over each year', value: Math.abs(cashOf(row)),
                vs: base
                    ? <><span className={`font-bold ${cDelta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(cDelta)} a year</span> · changing nothing: {cashWords(cashOf(base))}</>
                    : '',
                tone: cashOf(row) >= 0 ? (cDelta > 0 ? 'good' : 'neutral') : 'neutral',
                onWhy: () => setWhy({ row, page: 'cashflow' }),
            },
        ];
        const runway = row.projections?.length ? planRunwayCopy(row.projections as never, horizonYears) : null;
        // Every account in the order it is drawn, as every module's card says it (owner, 22 Oct 2026); the old line where that cannot be read.
        const cardLine = row.projections?.length ? runwayCardLine({ rows: row.projections as never, factFind, horizonYears, spentOn: cpSpentOnWords(cand(row)) }) : null;
        const runline = cardLine ? (
            <><strong className="text-slate-900">When the money runs out:</strong> {cardLine}</>
        ) : runway ? (
            <><strong className="text-slate-900">When the money runs out:</strong> {runway.depleted
                ? `${runway.headline.charAt(0).toLowerCase()}${runway.headline.slice(1)}.`
                : cashOf(row) >= 0 ? <>it doesn&apos;t — income covers the costs{cashOf(row) < 2000 ? `, with only ${money(cashOf(row))} a year to spare` : ' with room to spare'}.</>
                    : <>{runway.headline.charAt(0).toLowerCase()}{runway.headline.slice(1)}.</>}
            {runway.flag ? ` ${runway.flag}` : ''}</>
        ) : null;
        const lines: React.ReactNode[] = [];
        if (delta != null && delta < -1) {
            lines.push(<><strong className="text-rose-700">Important:</strong> it leaves the family <strong className="text-rose-700">{money(-delta)}</strong> less than changing nothing after about {yrs}. That is the price of the {pDelta >= 1 && cDelta >= 1 ? 'higher Age Pension and the extra each year' : cDelta >= 1 ? 'extra each year' : pDelta >= 1 ? 'higher Age Pension' : 'change'} it buys.</>);
        }
        if (m.annuity > 0 && m.annuityType !== 'guaranteed' && m.residual <= 0) {
            lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the guaranteed income is income only. It pays the most for life, and nothing from it goes back to the family, even after an early death.</>);
        } else if (m.annuity > 0) {
            lines.push(<><strong className="text-slate-900">Worth knowing:</strong> the {money(m.annuity)} is committed for life.{m.annuityType === 'guaranteed' ? ' It pays the least income of the three types, in return for the full amount back to the family.' : ' The money back to the family falls over time, to nothing later in life.'}</>);
        }
        if ((m.superTotal > 0 || m.investmentBond > 0) && Math.abs(pDelta) < 1) {
            lines.push(<><strong className="text-slate-900">Worth knowing:</strong> this is about tax, not the Age Pension: Centrelink counts the money the same way it counts the savings it replaces.</>);
        }
        const tradeOff = L.second && row.id === L.second.strategy.id && effective === 'legacy' ? secondPlanLine(L.second, (n) => money(n))
            : L.exception && row.id === L.estate?.id ? leadLine(L.exception, (n) => money(n)) : null;
        const key = String(row.id);
        const isChosen = chosenId != null && row.id === chosenId;
        return (
            <EnginePlanCard
                key={key}
                aligned
                testId="cp-plan-card"
                whyShown={tradeOff || null}
                chip={chipsFor(row)}
                chipTone="blue"
                title={w.title}
                sub={w.sub}
                legacy={estateOf(row)}
                legacyDelta={delta}
                horizonYears={horizonYears}
                baselineWord={benchmarkWords('cashflow').word}
                bars={delta != null ? { gainPct: (Math.abs(delta) / maxGain) * 100, tiedUp: tied, tiedUpPct: (tied / maxTied) * 100 } : null}
                tiedUpLabel="Put out of reach"
                hideEmptyTiedUp
                numbers={numbers}
                runline={runline}
                worthKnowing={lines.length ? <>{lines.map((l, i) => <p key={i}>{l}</p>)}</> : null}
                lines={[]}
                familyPanel={renderFamily(row)}
                chosen={isChosen}
                beforeChoose={beforeChoose(row)}
                chooseHeld={chooseHeld(row)}
                onExplain={() => onExplain(row)}
                onRisks={() => onRisks(row)}
                onChoose={() => onChoose(row)}
                chooseLabel={chosenId != null && !isChosen ? 'Choose this instead' : 'Choose this plan'}
                chosenStrip={<ChosenAdviceLine possessiveFile={v.possessive} onAdvice={() => onAdvice(row)} onCustomise={() => onBuildYourOwn(row)} />}
                detail={renderDetail(row)}
                detailOpen={openDetail === key}
                onToggleDetail={() => setOpenDetail((d) => (d === key ? null : key))}
                detailHint="where the money comes from and the terms"
            />
        );
    };

    const Strip = () => (
        <div data-testid="cp-nothing-strip">
            <p className="text-[13px] font-semibold text-slate-500">Compared with changing nothing — the four numbers from step 1</p>
            <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded-xl border border-slate-200 bg-slate-200 shadow-card lg:grid-cols-4">
                {[
                    { label: 'Age Pension', value: money(pensionOf(base)), note: 'a year' },
                    { label: cashOf(base) < 0 ? 'Each year, short by' : 'Left over each year', value: money(Math.abs(cashOf(base))), note: supportCost ? `with ${money(supportCost.annual)} of support` : 'money in, less money out', tone: cashOf(base) >= 0 ? 'text-emerald-700' : 'text-rose-700' },
                    { label: `What ${v.they} own`, value: money(ownToday), note: 'today' },
                    { label: 'Left to the family', value: money(estateOf(base)), note: `after about ${yrs}` },
                ].map((c) => (
                    <div key={c.label} className="bg-white px-4 py-3">
                        <p className="text-[12px] font-semibold text-slate-500">{c.label}</p>
                        <p className={`mt-0.5 text-[22px] font-bold leading-tight tabular-nums ${c.tone || 'text-slate-900'}`}>{c.value}</p>
                        <p className="text-[12px] text-slate-500">{c.note}</p>
                    </div>
                ))}
            </div>
        </div>
    );

    const top = L.estate;
    const topDelta = top && base ? estateOf(top) - estateOf(base) : 0;
    const verdict = base && top ? (
        L.estateBeats ? (
            <><strong className="text-slate-900">The best way leaves the family {money(topDelta)} more than changing nothing after about {yrs}</strong>
                {L.pension ? <>, and the most it can lift {v.possessive} Age Pension is {money(pensionOf(L.pension) - pensionOf(base))} a year</> : null}. We tested {tested} ways; below are the strongest.</>
        ) : (
            <><strong className="text-slate-900">No way we tested leaves the family more than changing nothing.</strong>{' '}
                The closest leaves {money(-topDelta)} less after about {yrs}{L.pension ? <>; {v.they} can still lift the Age Pension by up to {money(pensionOf(L.pension) - pensionOf(base))} a year, at a cost to the estate</> : null}. Changing nothing is a real answer; the cards show what each change would buy.</>
        )
    ) : null;

    const pensionWhyBase = cpPensionWhyOf(base?.projections?.[0], isCouple);

    return (
        <div className="space-y-6" data-testid="cp-plans">
            {notices}
            {base && <Strip />}
            {verdict && (
                <div className={`rounded-xl border border-slate-200 border-l-4 bg-white px-4 py-3 text-[14px] leading-relaxed text-slate-700 shadow-card ${L.estateBeats ? 'border-l-emerald-500' : 'border-l-brand-navy'}`} data-testid="cp-verdict">{verdict}</div>
            )}

            <EngineGoalControl
                value={focus}
                onChange={onFocus}
                household={v.names}
                enteringName={v.names}
                horizonYears={horizonYears}
                coveredSameAsBest={coveredSame}
                bestCovers={coveredSame && cashOf(L.estate) >= 0}
                waysTested={tested}
                shown={shown.length}
                testedFor="without moving house"
                closingNote={<>Each card says when the savings would run out, and how much money it puts out of reach.</>}
                subs={{
                    legacy: L.estateBeats ? `Biggest estate after about ${yrs}` : 'Nothing beats changing nothing',
                    covered: coveredSame ? 'Same plan as "leave the most"' : 'Pays its way without savings',
                    pension: L.pension ? 'Most government support' : 'No plan lifts it — see why',
                }}
                countLine={noPensionLift ? 'No plan lifts the Age Pension.' : shown.length === 0 ? 'None leads on this goal.' : undefined}
            />

            {noPensionLift ? (
                <div className="rounded-2xl border border-slate-200 border-l-4 border-l-brand-navy bg-white p-6 shadow-card" data-testid="cp-no-pension">
                    <h3 className="text-[19px] font-bold text-slate-900">No plan lifts {v.possessive} Age Pension</h3>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">
                        Changing nothing, {v.they} get{v.s} <strong>{money(pensionOf(base))} a year</strong>. {pensionWhyBase ? cpPensionWhyWords(pensionWhyBase, v.their) : ''}
                    </p>
                    <p className="mt-2 text-[15px] leading-relaxed text-slate-700">None of the ways we tested moves enough out of Centrelink&apos;s count to change that by more than $100 a year, so this goal can&apos;t tell the plans apart. Choose another goal to compare them.</p>
                    <div className="mt-4"><button type="button" onClick={() => onFocus('legacy')} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300">Compare the plans on another goal</button></div>
                </div>
            ) : shown.length === 0 ? (
                <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-card" data-testid="cp-no-leader">
                    <h3 className="text-[19px] font-bold text-slate-900">{L.usable.length ? 'No plan leads on this goal' : 'On these figures there is nothing we would change'}</h3>
                    <p className="mt-2 text-[14px] leading-relaxed text-slate-700">
                        {L.usable.length
                            ? <>None of the ways we tested beats changing nothing on this goal. Choose another goal above, or compare all {tested} ways.</>
                            : <>That is a real answer, not a gap: these ways need savings beyond the money kept for emergencies to be worth doing.</>}
                    </p>
                </div>
            ) : (
                <div className={`grid grid-cols-1 gap-5 ${shown.length > 1 ? 'lg:grid-cols-2' : ''}`} data-testid="cp-plan-cards">
                    {chosenId != null && !shown.some((s) => s.id === chosenId) && (
                        <div className={shown.length > 1 ? 'lg:col-span-2' : ''}><ChosenAdviceLine possessiveFile={v.possessive} onAdvice={() => { const row = results.find((r) => r.id === chosenId); if (row) onAdvice(row); }} offCard testId="chosen-plan-off-card" /></div>
                    )}
                    {shown.map(card)}
                </div>
            )}
            {shown.length > 0 && <OtherGoalLeaders others={others} here={{ legacy: estateOf(shown[0]!), cashflow: cashOf(shown[0]!), pension: pensionOf(shown[0]!) }} baseLegacy={base ? estateOf(base) : null} baseWord="changing nothing" onFocus={onFocus} testId="cp-other-leaders" />}

            <div className="flex flex-wrap items-center gap-3" data-testid="cp-ways-on">
                <button type="button" onClick={() => setComparing(true)} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="cp-compare-all">Compare all {tested} side by side</button>
                <button type="button" onClick={onChanges} className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-[14px] font-semibold text-slate-800 hover:border-blue-300" data-testid="cp-changes">Things that change the answer</button>
                <button type="button" onClick={() => onBuildYourOwn(null)} className="text-[14px] font-semibold text-brand-navy underline decoration-slate-300 underline-offset-4 hover:decoration-brand-navy" data-testid="cp-build-your-own">Prefer to set the numbers yourself? Build a plan →</button>
            </div>
            <p className="text-[13px] text-slate-500" data-testid="all-plans-open-line">{allPlansOpenLine(tested)}</p>
            <p className="text-[13px] text-slate-500">Each figure is the total that plan gives, with the change against changing nothing beneath it. Figures use the rates in force today and are projections over {yrs}, not guarantees. Moving house — downsizing, a retirement village or a granny flat — is modelled in its own section.</p>

            {comparing && (
                <DialogShell titleId="cp-compare-title" title={`All ${tested} ways side by side, and changing nothing`} eyebrow={`Over about ${yrs}, each against changing nothing`} onClose={() => setComparing(false)} size="xl" testId="cp-compare-dialog" flush>
                    <div className="overflow-x-auto">
                        <table className="phone-stack min-w-full text-[14px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-left text-[12px] font-semibold text-slate-500">
                                    <th className="px-5 py-3">Plan</th>
                                    <th className="px-5 py-3 text-right">Left to the family</th>
                                    <th className="px-5 py-3 text-right">Age Pension</th>
                                    <th className="px-5 py-3 text-right">Each year</th>
                                    <th className="px-5 py-3 text-right">Put out of reach</th>
                                    <th className="px-5 py-3"> </th>
                                </tr>
                            </thead>
                            <tbody className="tabular-nums">
                                {results.map((r) => {
                                    const errored = !!r.error;
                                    const isChosen = chosenId != null && r.id === chosenId;
                                    const held = !errored && chooseHeld(r);
                                    return (
                                        <tr key={String(r.id)} className={`border-b border-slate-100 align-top ${r.changeNothing ? 'text-slate-500' : ''}`}>
                                            <td className="px-5 py-3">
                                                <strong className={r.changeNothing ? 'text-slate-600' : 'text-slate-900'}>{r.changeNothing ? 'Change nothing' : words(r).title}</strong>
                                                {!r.changeNothing && chipsFor(r) && <span className="block text-[12px] font-semibold text-brand-navy">{chipsFor(r)}</span>}
                                                {errored && <span className="mt-1 block text-[12.5px] text-slate-600" data-testid="cp-errored-row">{CANNOT_CHOOSE_ERRORED}</span>}
                                            </td>
                                            {errored ? <td className="px-5 py-3 text-right" colSpan={4}>—</td> : (
                                                <>
                                                    <td data-label="Left to the family" className="px-5 py-3 text-right font-semibold">{money(estateOf(r))}{!r.changeNothing && base && <span className={`block text-[12px] ${estateOf(r) >= estateOf(base) ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(estateOf(r) - estateOf(base))}</span>}</td>
                                                    <td data-label="Age Pension" className="px-5 py-3 text-right">{money(pensionOf(r))}{!r.changeNothing && base && Math.abs(pensionOf(r) - pensionOf(base)) >= 1 && <span className={`block text-[12px] ${pensionOf(r) > pensionOf(base) ? 'text-emerald-700' : 'text-rose-700'}`}>{signed(pensionOf(r) - pensionOf(base))}</span>}</td>
                                                    <td data-label="Each year" className={`px-5 py-3 text-right ${cashOf(r) < 0 ? 'text-rose-700' : ''}`}>{cashWords(cashOf(r))}</td>
                                                    <td data-label="Put out of reach" className="px-5 py-3 text-right">{r.changeNothing ? '—' : money(cpTiedUpOf(cand(r)))}</td>
                                                </>
                                            )}
                                            <td className="whitespace-nowrap px-5 py-3 text-right">
                                                {!r.changeNothing && (
                                                    <button
                                                        type="button"
                                                        disabled={errored || held}
                                                        aria-disabled={errored || held}
                                                        onClick={() => { if (!errored && !held) { onChoose(r); setComparing(false); } }}
                                                        className={`rounded-lg px-3 py-1.5 text-[13px] font-semibold ${isChosen ? 'border border-emerald-500 bg-white text-emerald-800' : errored || held ? 'cursor-not-allowed bg-slate-200 text-slate-500' : 'bg-blue-600 text-white hover:bg-blue-700'}`}
                                                    >
                                                        {isChosen ? 'Chosen ✓' : 'Choose this plan'}
                                                    </button>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                    <p className="px-5 py-3 text-[13px] text-slate-500">A plan with a guaranteed income that pays nothing back can be chosen once {v.you ? 'you have' : `${v.names} ${v.have}`} confirmed it on its card.</p>
                </DialogShell>
            )}

            {why && (
                <DialogShell
                    titleId="cp-why-title"
                    eyebrow={words(why.row).title}
                    title={why.page === 'pension' ? 'Why the Age Pension is what it is' : 'Left over each year'}
                    onClose={() => setWhy(null)}
                    testId="cp-why-dialog"
                >
                    {why.page === 'pension' ? (() => {
                        const w = cpPensionWhyOf(why.row.projections?.[0], isCouple);
                        return (
                            <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                                <p>Changing nothing: <strong>{money(pensionOf(base))} a year</strong>. With this plan: <strong>{money(pensionOf(why.row))} a year</strong> in the first year.</p>
                                {w && <p data-testid="cp-why-pension-count">With this plan, {cpPensionWhyWords(w, v.their)}</p>}
                                <p className="text-slate-600">A funeral bond within the limit and a gift within the free area are not counted at all; a guaranteed income is counted at 60% of what went in (30% later); super and an investment bond are counted like the savings they replace.</p>
                            </div>
                        );
                    })() : (
                        <div className="space-y-3 text-[14px] leading-relaxed text-slate-700">
                            <p>Money in, less money out, in the first year: <strong>{cashWords(cashOf(why.row))}</strong>. Changing nothing: {cashWords(cashOf(base))}.</p>
                            <div className="text-[13px] text-slate-600"><BreakdownList rows={why.row.projections?.[0]?.cashFlowBreakdown} /></div>
                        </div>
                    )}
                </DialogShell>
            )}
        </div>
    );
};

export default CpPlansView;
